import { GridSortItem } from "@mui/x-data-grid";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useRef } from "react";
import { ChatMessageControllerApi } from "@/services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  useBaseContent,
} from "@/utils/utils";

interface Props {
  pagination: DGT_Pagination;
  gridSorting?: GridSortItem[];
  archive?: boolean;
  conversationId?: string;
  xTENANTID?: string;
}

export type OptionData = {
  name: string;
  uuid: string;
};

/**
 * @author [Rajkumar Rathod]
 */
export const useChatGetAll = ({
  pagination,
  gridSorting,
  archive,
  conversationId,
  xTENANTID,
}: Props): useBaseContent<OptionData> => {
  const prevPage = useRef<number>(pagination.page);
  const queryClient = useQueryClient();

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey: "",
    prevPage,
  });
  const pageSize = getPageSize("", pagination);

  const sortDirect = gridSorting?.[0]?.sort ?? undefined;
  const sortBy = sortDirect ? "firstName" : undefined;

  return useQuery({
    queryKey: [
      "chat-list",
      pageIndex,
      pageSize,
      gridSorting,
      archive,
      conversationId,
      xTENANTID,
    ],
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: !!xTENANTID && !!conversationId,
    queryFn: async () => {
      const res = await new ChatMessageControllerApi(
        getConfigObject()
      ).getAllChatMessage(
        pageIndex,
        pageSize,
        sortBy,
        sortDirect,
        archive,
        conversationId,
        xTENANTID
      );

      // Fetching a conversation's messages marks them read, so the Chat tab total must be re-read.
      queryClient.invalidateQueries({ queryKey: ["chat-unread-count"] });

      return res?.data;
    },
  });
};
